import {
  ISORegionCode,
  SUPPORTED_REGION_CODE_MAP,
} from '@principle-theorem/principle-core/interfaces';
import { NON_DIGITS, Region } from '@principle-theorem/shared';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { normaliseEmail } from '../helpers';
import { isEmail } from '../models/patient/patient-contact-details';

const topLevelDomain = /\.[a-z]{2,}$/;

/** Reserved by RFC 2606 for documentation, so never a patient's real address. */
const reservedEmailDomains =
  /(^|\.)(example\.(com|org|net)|example|test|invalid|localhost)$/;

/**
 * What reception types when a patient has no email. The addresses are otherwise
 * well-formed, so only the local part gives them away.
 */
const placeholderEmailLocalParts =
  /^(no|none|nil|na|nothing|noemail|fake|dummy|unknown|test|need|getemail)([._-]?e?mail)?$/;

/**
 * Canonical comparison keys for patient contact details.
 *
 * Display helpers such as `formatPhoneNumber` fall back to a digits-only string
 * when a value won't parse. That is fine on screen but wrong for grouping: junk
 * survives as a key, and the digits-only fallback can produce a second key for a
 * number that elsewhere resolves to E.164, hiding a genuine duplicate. These
 * methods return `undefined` instead, so anything that cannot be canonicalised
 * is never compared against another patient.
 */
export class DuplicateContactKey {
  /**
   * Placeholder addresses are shared by hundreds of patients, so admitting one as
   * a key buries every genuine duplicate underneath it.
   */
  static forEmail(email?: string): string | undefined {
    if (!email) {
      return undefined;
    }
    const normalised = normaliseEmail(email);
    if (!isEmail(normalised) || !this._isRealAddress(normalised)) {
      return undefined;
    }
    return normalised;
  }

  /**
   * Retries on the digits alone so numbers annotated by staff, such as
   * `0412 345 678 (mum)`, still match the same number recorded cleanly.
   */
  static forPhoneNumber(
    contactNumber: string,
    region: Region
  ): string | undefined {
    const regionCode = SUPPORTED_REGION_CODE_MAP[region];
    return (
      this._toE164(contactNumber, regionCode) ??
      this._toE164(contactNumber.replace(NON_DIGITS, ''), regionCode)
    );
  }

  private static _isRealAddress(normalisedEmail: string): boolean {
    const [localPart, domain] = normalisedEmail.split('@');
    if (!domain || !topLevelDomain.test(domain)) {
      return false;
    }
    return (
      !reservedEmailDomains.test(domain) &&
      !placeholderEmailLocalParts.test(localPart)
    );
  }

  private static _toE164(
    contactNumber: string,
    regionCode: ISORegionCode
  ): string | undefined {
    if (!contactNumber) {
      return undefined;
    }
    const parsed = parsePhoneNumber(contactNumber, { regionCode });
    return parsed.valid ? parsed.number?.e164 : undefined;
  }
}
